import { MesfModal, useHasPermission } from "@dexteel/mesf-core";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Collapse,
	Divider,
	FormControlLabel,
	IconButton,
	InputAdornment,
	Link,
	MenuItem,
	Switch,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CREWS, SHOW_IN_PLC } from "../../straightener-common/constants";
import { useStraightenerParameters } from "../../straightener-common/hooks/useStraightenerParameters";
import {
	GROUP_LABELS,
	type StraightenerParameter,
} from "../../straightener-common/parameterCatalog";
import { useStraightenerParamLimits } from "../../straightener-common/hooks/useStraightenerParamLimits";
import {
	buildLimitOverrides,
	isOutOfLimit,
	limitRangeLabel,
	resolveLimit,
} from "../../straightener-common/paramLimits";
import { NumericStepField } from "../../straightener-common/components/NumericStepField";
import { FIELD_SX, FIELD_WIDTH } from "../../straightener-common/fieldStyles";
import { PERMISSION_STRAIGHTENER_LIMITS } from "../../straightener-common/permissions";
import type { PrefillMode } from "../../straightener-common/hooks/useStraightenerRecipeEditor";
import { UnsavedChangesModal } from "../../straightener-common/components/UnsavedChangesModal";
import type {
	StraightenerProduct,
	StraightenerRecipeValueRow,
} from "../models/StraightenerRecipe";
import { useRecipeValidation } from "../hooks/useRecipeValidation";
import {
	type Cell,
	PRODUCT_DIM_CODES,
	cellKey as key,
	emptyCell,
	numOrNull,
	productDimValues,
	rollsOf,
	toStr,
} from "./recipeFormUtils";
import {
	type RollBand,
	StraightenerRollsTable,
} from "./StraightenerRollsTable";
import { EDITABLE_FIELD_SX } from "@/pages/reports/rollshop-operations/components/compactDialog";

interface Props {
	product: StraightenerProduct | null;
	recipeId: number | null;
	version: number | null;
	values: StraightenerRecipeValueRow[];
	isLoadingValues: boolean;
	isSaving: boolean;
	defaultCrew?: string;
	defaultName?: string;
	defaultComments?: string;
	// Names of the product's other recipes (lower-cased), for the unique-per-
	// product check. Excludes the recipe being edited.
	existingNames?: string[];
	onSave: (
		crew: string | null,
		name: string | null,
		comments: string | null,
		values: StraightenerRecipeValueRow[],
	) => void;
	onSaveAndSetCurrent?: (
		crew: string | null,
		name: string | null,
		comments: string | null,
		values: StraightenerRecipeValueRow[],
	) => void;
	onSaveCurrent?: (
		crew: string | null,
		name: string | null,
		comments: string | null,
		values: StraightenerRecipeValueRow[],
	) => void;
	onPrefill?: (mode: PrefillMode) => void;
	copySourceLabel?: string | null;
	isCurrent?: boolean;
	isPlcCurrent?: boolean;
	isActive?: boolean;
	isDraft?: boolean;
	onSetCurrent?: () => void;
	onSetPlcCurrent?: () => void;
	onToggleActive?: (active: boolean) => void;
	// Inline (Trend Display) mode: render a collapse arrow by the title and hide
	// the top-right close X (the arrow becomes the single collapse affordance).
	collapsible?: boolean;
	// Title for the new-recipe variant (e.g. "Duplicate Recipe"); defaults to
	// "New Recipe".
	newRecipeTitle?: string;
	// Read Only: all fields disabled, all write actions hidden.
	readOnly?: boolean;
	// Reports the editor's unsaved-changes state so the parent can guard tree
	// navigation with the same warning.
	onDirtyChange?: (dirty: boolean) => void;
	onClose: () => void;
}

const sectionHeaderSx = {
	color: "primary.main",
	fontWeight: 600,
	fontSize: "0.85rem",
	mt: 1.5,
	mb: 0.5,
};

const cardSx = {
	p: 1,
	border: "1px solid",
	borderColor: "divider",
	borderRadius: 1,
	backgroundColor: "background.paper",
} as const;

const cardHeaderSx = {
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between",
	cursor: "pointer",
	userSelect: "none",
} as const;

// Past five fields a row stops reading as a row: the eye loses which value
// belongs to which label on the way across. Wider cards get wider fields, not
// more of them.
const MAX_FIELD_COLUMNS = 5;
const FIELD_GAP = 8;
const FIELD_GRID_FULL =
	MAX_FIELD_COLUMNS * FIELD_WIDTH + (MAX_FIELD_COLUMNS - 1) * FIELD_GAP;

// The field groups under the rolls table, in the order the line runs them.
const FIELD_GROUP_ORDER = [
	"Entry",
	"CoolingBed",
	"CutToLength",
	"SpeedDiameters",
] as const;

const START_FROM_OPTIONS: { mode: PrefillMode; label: string }[] = [
	{ mode: "current", label: "Current line values" },
	{ mode: "copy", label: "Copy from other recipe" },
	{ mode: "blank", label: "Blank" },
];
export const StraightenerRecipeDetailViewer = ({
	product,
	recipeId,
	version,
	values,
	isLoadingValues,
	isSaving,
	defaultCrew,
	defaultName,
	defaultComments,
	existingNames,
	onSave,
	onSaveAndSetCurrent,
	onSaveCurrent,
	onPrefill,
	copySourceLabel,
	isCurrent,
	isPlcCurrent,
	isActive,
	isDraft,
	onSetCurrent,
	onSetPlcCurrent,
	onToggleActive,
	collapsible,
	newRecipeTitle,
	readOnly = false,
	onDirtyChange,
	onClose,
}: Props) => {
	const { byGroup: groups, rollCount: catalogRollCount } =
		useStraightenerParameters();
	const perRollParams = useMemo(
		() => groups.StraighteningRolls ?? [],
		[groups],
	);
	// Non per-roll groups, rendered as cards packed into responsive columns.
	// Read-only (PLC read-back) params are excluded: a recipe only holds values
	// that get written to the PLC.
	const fieldSections = useMemo(
		() =>
			Object.values(groups)
				.map((list) => list.filter((p) => !p.readOnly))
				.filter((list) => list.length > 0 && !list.some((pp) => pp.isPerRoll)),
		[groups],
	);
	const sectionOf = (group: string) =>
		fieldSections.find((l) => l[0].group === group) ?? null;
	// The dimensions box adapts to the product's real geometry (mirrors legacy
	// RFAC.MapDimensionsToSize): flats (no Dim3) have only width + thickness —
	// their height IS the thickness, so the field is hidden (the value is still
	// stored/sent under the hood).
	const dimensionsSection = useMemo(() => {
		const list = fieldSections.find((l) => l[0].group === "Dimensions");
		if (!list) return null;
		return list.filter(
			(p) => p.code !== "ProductHeight" || product?.Dim3 != null,
		);
	}, [fieldSections, product?.Dim3]);
	const [crew, setCrew] = useState<string>("");
	const [name, setName] = useState<string>("");
	const [comments, setComments] = useState<string>("");
	const [cells, setCells] = useState<Record<string, Cell>>({});
	const [prefillMode, setPrefillMode] = useState<PrefillMode>("current");
	// Name errors stay hidden until the first save attempt, then follow the input.
	const [saveAttempted, setSaveAttempted] = useState(false);
	// Snapshot of the form as last loaded/saved, to detect unsaved changes.
	const cleanRef = useRef<string>("");
	const [confirmClose, setConfirmClose] = useState(false);
	// Start-from mode pending confirmation because the form has unsaved edits
	// that re-seeding would replace.
	const [confirmPrefill, setConfirmPrefill] = useState<PrefillMode | null>(
		null,
	);
	// Per-section collapse (all expanded by default).
	const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
	const toggleSection = (group: string) =>
		setCollapsed((prev) => ({ ...prev, [group]: !prev[group] }));

	// Effective limits: backend (Configuration > Straightener) overrides, with the
	// static catalog as fallback until they load.
	const { data: limitRows } = useStraightenerParamLimits();
	const limitOverrides = useMemo(
		() => buildLimitOverrides(limitRows),
		[limitRows],
	);
	// Required flags come from the backend catalog (Configuration > Straightener).
	const requiredCodes = useMemo(() => {
		const set = new Set<string>();
		for (const r of limitRows ?? [])
			if (r.IsRequired && !r.IsReadOnly) set.add(r.Code);
		return set;
	}, [limitRows]);
	// Shortcut to the (global) parameter limits config in a new tab, gated only by
	// its permission — the limits are product-agnostic, so it shows even read-only.
	const hasPermissionTo = useHasPermission();
	const canOpenParameters = hasPermissionTo(PERMISSION_STRAIGHTENER_LIMITS);
	const openParameters = useCallback(() => {
		window.open("/configuration/straightener-limits", "_blank", "noopener");
	}, []);
	// Roll count is catalog data (RollCount per parameter), not a constant:
	// adding a roll is an UPDATE, not a code change.
	const rollCount = useMemo(() => {
		const counts = (limitRows ?? [])
			.filter((r) => r.RollCount > 0)
			.map((r) => r.RollCount);
		return counts.length ? Math.max(...counts) : catalogRollCount;
	}, [limitRows, catalogRollCount]);

	useEffect(() => {
		const next: Record<string, Cell> = {};
		for (const list of Object.values(groups)) {
			for (const p of list) {
				// The band is shown as it arrives: stored for an existing recipe,
				// blank for a new one (the seeding sources strip it, see
				// withoutBand). The parameter limits are sanity bounds, not a
				// tolerance, so they no longer pre-fill it.
				for (const roll of rollsOf(p, rollCount)) {
					const found = values.find(
						(v) => v.ParameterCode === p.code && v.RollNumber === roll,
					);
					// Round per-roll values and Working Diameter to 3 decimals (strips
					// float noise, keeps the full PLC precision); everything else is
					// shown as stored.
					const round3dp = p.isPerRoll || p.code === "WorkingDiameter";
					// Selects carry no phantom default: an unsaved field stays empty
					// (and counts as missing when required) until a value is chosen.
					// Rows may carry only the numeric option value (e.g. the
					// current-line prefill) — resolve the option text from it.
					const selectText =
						p.valueType === "select" && found
							? (found.TextValue ??
								p.options?.find((o) => o.numeric === found.Value)?.value ??
								"")
							: (found?.TextValue ?? "");
					next[key(p.code, roll)] = {
						value: found ? toStr(found.Value, round3dp) : "",
						min: toStr(found?.MinValue ?? null, round3dp),
						max: toStr(found?.MaxValue ?? null, round3dp),
						text: selectText,
					};
				}
			}
		}
		// Product dimensions are fixed by the product: override with the
		// type-aware Dim1/2/3 mapping (auto, disabled) so they always reflect
		// the product and are saved.
		for (const [code, dim] of Object.entries(productDimValues(product))) {
			const k = key(code, 0);
			next[k] = {
				...(next[k] ?? emptyCell()),
				value: dim != null ? String(dim) : "",
			};
		}
		setCells(next);
		setCrew(defaultCrew ?? "");
		setName(defaultName ?? "");
		setComments(defaultComments ?? "");
		cleanRef.current = JSON.stringify({
			crew: defaultCrew ?? "",
			name: defaultName ?? "",
			comments: defaultComments ?? "",
			cells: next,
		});
	}, [
		recipeId,
		values,
		groups,
		rollCount,
		defaultCrew,
		defaultName,
		defaultComments,
		product?.Dim1,
		product?.Dim2,
		product?.Dim3,
	]);

	// Reset the "Start from" selector to the default when a different recipe /
	// product opens (not when the prefill re-seeds the values).
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset only on recipe/product switch
	useEffect(() => {
		setPrefillMode("current");
		setSaveAttempted(false);
	}, [recipeId, product?.Id]);

	const update = (k: string, patch: Partial<Cell>) =>
		setCells((prev) => ({ ...prev, [k]: { ...prev[k], ...patch } }));

	const isDirty = () =>
		cleanRef.current !== JSON.stringify({ crew, name, comments, cells });

	// Report unsaved-changes state upward so the parent can guard tree navigation.
	useEffect(() => {
		onDirtyChange?.(
			cleanRef.current !== JSON.stringify({ crew, name, comments, cells }),
		);
	}, [crew, name, comments, cells, onDirtyChange]);

	// Apply a "Start from" choice (re-seeds the form). Copy opens its own modal.
	const applyPrefill = (mode: PrefillMode) => {
		setPrefillMode(mode);
		onPrefill?.(mode);
	};
	// Re-seeding replaces the current values, so warn first when the form is dirty.
	const requestPrefill = (mode: PrefillMode) => {
		if (isDirty()) setConfirmPrefill(mode);
		else applyPrefill(mode);
	};

	// Validation (limits / required / bands) — see useRecipeValidation.
	const {
		trimmedName,
		nameError,
		hasLimitErrors,
		missingRequired,
		missingCount,
		bandInvalid,
		targetOutsideBand,
		bandErrorCount,
		saveBlocked,
		blockErrorMessage,
		draftMessage,
	} = useRecipeValidation({
		cells,
		fieldSections,
		perRollParams,
		rollCount,
		requiredCodes,
		limitOverrides,
		name,
		existingNames,
	});

	const renderField = (p: StraightenerParameter) => {
		const k = key(p.code, 0);
		const c = cells[k] ?? emptyCell();
		if (p.valueType === "select") {
			const selectMissing =
				!readOnly && requiredCodes.has(p.code) && !(c.text ?? "");
			return (
				<TextField
					key={p.code}
					select
					label={p.displayName}
					value={c.text}
					disabled={readOnly}
					error={selectMissing}
					helperText={selectMissing ? "Required" : undefined}
					onChange={(e) => update(k, { text: e.target.value })}
					fullWidth
					size="small"
					sx={[
						...(Array.isArray(FIELD_SX) ? FIELD_SX : [FIELD_SX]),
						EDITABLE_FIELD_SX,
					]}
					slotProps={{ inputLabel: { shrink: true } }}
				>
					{(p.options ?? []).map((o) => (
						<MenuItem key={o.value} value={o.value}>
							{o.label}
						</MenuItem>
					))}
				</TextField>
			);
		}
		// Product dimensions are fixed by the product — auto-filled and disabled.
		const isProductDim = PRODUCT_DIM_CODES.has(p.code);
		const disabled = p.readOnly || isProductDim || readOnly;
		const bad =
			!disabled && isOutOfLimit(p.code, numOrNull(c.value), limitOverrides);
		const missing =
			!p.readOnly &&
			!readOnly &&
			requiredCodes.has(p.code) &&
			numOrNull(c.value) == null;
		// The out-of-range hint lives inside the field (icon + tooltip) so it never
		// reflows the layout the way a helperText does.
		const endAdornment = bad ? (
			<InputAdornment position="end" sx={{ ml: 0, gap: 0.5 }}>
				<Tooltip
					title={`Out of range (${limitRangeLabel(p.code, limitOverrides)}) · check the limits page`}
				>
					<WarningAmberIcon sx={{ fontSize: "1.05rem", color: "error.main" }} />
				</Tooltip>
			</InputAdornment>
		) : undefined;
		const limit = resolveLimit(p.code, limitOverrides);
		return (
			<NumericStepField
				key={p.code}
				label={p.displayName}
				value={c.value}
				disabled={disabled}
				error={bad || missing}
				onValueChange={(v) => update(k, { value: v })}
				fullWidth
				endAdornment={endAdornment}
				min={limit.min}
				max={limit.max}
				helperText={missing && !isProductDim ? "Required" : undefined}
			/>
		);
	};

	const renderCard = (list: StraightenerParameter[]) => {
		const group = list[0].group;
		const isCollapsed = !!collapsed[group];
		// auto-fill (not auto-fit) keeps the empty tracks, so a card with two
		// fields shows two fields — the tracks stretch to use the card's width,
		// they do not stretch the fields across it. Once the card is wide enough
		// for the full five, the count is pinned there and the tracks take the
		// slack; CSS cannot cap auto-fill on its own.
		const gridSx = {
			display: "grid",
			gap: `${FIELD_GAP}px`,
			gridTemplateColumns: `repeat(auto-fill, minmax(${FIELD_WIDTH}px, 1fr))`,
			[`@container (min-width: ${FIELD_GRID_FULL}px)`]: {
				gridTemplateColumns: `repeat(${MAX_FIELD_COLUMNS}, minmax(0, 1fr))`,
			},
		} as const;
		return (
			// Its own query container, so the cap above is measured against the
			// card and not against the whole editor body.
			<Box sx={{ ...cardSx, containerType: "inline-size" }}>
				<Box onClick={() => toggleSection(group)} sx={cardHeaderSx}>
					<Typography sx={{ ...sectionHeaderSx, mt: 0, mb: 0 }}>
						{GROUP_LABELS[group]}
					</Typography>
					<IconButton size="small" sx={{ p: 0.25, color: "text.secondary" }}>
						{isCollapsed ? (
							<ExpandMoreIcon fontSize="small" />
						) : (
							<ExpandLessIcon fontSize="small" />
						)}
					</IconButton>
				</Box>
				<Collapse in={!isCollapsed} unmountOnExit>
					<Box sx={{ pt: 0.75 }}>
						<Box sx={gridSx}>{list.map(renderField)}</Box>
					</Box>
				</Collapse>
			</Box>
		);
	};

	// Guard close/collapse/cancel: warn if there are unsaved changes.
	const requestClose = () => {
		if (isDirty()) setConfirmClose(true);
		else onClose();
	};

	const getRollCell = useCallback(
		(code: string, roll: number, band: RollBand) =>
			cells[key(code, roll)]?.[band] ?? "",
		[cells],
	);
	const setRollCell = useCallback(
		(code: string, roll: number, band: RollBand, value: string) => {
			setCells((prev) => {
				const k = key(code, roll);
				const c = prev[k] ?? emptyCell();
				return { ...prev, [k]: { ...c, [band]: value } };
			});
		},
		[],
	);

	// Every cell honors the parameter's limits; Min/Max must satisfy min < max;
	// Target must sit inside the band.
	const rollCellError = useCallback(
		(code: string, roll: number, band: RollBand): string | null => {
			const v = numOrNull(getRollCell(code, roll, band));
			if (band === "value" && v == null)
				return requiredCodes.has(code) && !readOnly ? "Required" : null;
			if (v != null && isOutOfLimit(code, v, limitOverrides))
				return `Out of range (${limitRangeLabel(code, limitOverrides)}) · check the limits page`;
			const mn = numOrNull(getRollCell(code, roll, "min"));
			const mx = numOrNull(getRollCell(code, roll, "max"));
			if (band === "value" && targetOutsideBand(code, roll)) {
				const range =
					mn != null && mx != null
						? `${mn}–${mx}`
						: mn != null
							? `≥ ${mn}`
							: `≤ ${mx}`;
				return `Target (${v}) must be within ${range}`;
			}
			if (band !== "value" && bandInvalid(code, roll))
				return `Min (${mn}) must be less than Max (${mx})`;
			return null;
		},
		[
			getRollCell,
			requiredCodes,
			readOnly,
			limitOverrides,
			targetOutsideBand,
			bandInvalid,
		],
	);

	const buildValues = (): StraightenerRecipeValueRow[] => {
		const out: StraightenerRecipeValueRow[] = [];
		for (const list of Object.values(groups)) {
			for (const p of list) {
				for (const roll of rollsOf(p, rollCount)) {
					const c = cells[key(p.code, roll)] ?? emptyCell();
					out.push({
						ParameterCode: p.code,
						RollNumber: roll,
						// Selects store the option's numeric PLC value + its text.
						Value:
							p.valueType === "select"
								? (p.options?.find((o) => o.value === c.text)?.numeric ?? null)
								: numOrNull(c.value),
						MinValue: p.hasMinMax ? numOrNull(c.min) : null,
						MaxValue: p.hasMinMax ? numOrNull(c.max) : null,
						TextValue: p.valueType === "select" ? c.text || null : null,
					});
				}
			}
		}
		return out;
	};

	const commentsOrNull = () => comments.trim() || null;

	const shownNameError = saveAttempted ? nameError : null;
	const withValidName = (save: () => void) => {
		setSaveAttempted(true);
		if (!nameError) save();
	};

	const handleSaveCurrent = () => {
		if (!product) return;
		withValidName(() =>
			onSaveCurrent?.(
				crew || null,
				name || null,
				commentsOrNull(),
				buildValues(),
			),
		);
	};

	if (!product) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: "text.secondary",
				}}
			>
				<Typography>Select a recipe to view details</Typography>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				height: "100%",
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
			}}
		>
			{/* Title bar: title on the left, recipe-level actions + close on the
			    right — all aligned on one row. */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 1.5,
					px: 2,
					py: 1,
					flexWrap: "wrap",
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 0.5,
						flexWrap: "wrap",
					}}
				>
					<Typography
						variant="subtitle2"
						sx={{
							fontWeight: 700,
							color: "text.primary",
							letterSpacing: "0.05em",
							textTransform: "uppercase",
						}}
					>
						{recipeId ? (
							<>
								{`Edit Recipe: (${product.ProductTypeName}) ${product.Name} · ${name}${
									crew ? ` · Crew ${crew}` : ""
								}`}
								<Box
									component="span"
									sx={{ color: "text.disabled", ml: 0.5, fontWeight: 400 }}
								>
									{`· v${version ?? ""}`}
								</Box>
							</>
						) : (
							`${newRecipeTitle ?? "New Recipe"}: (${product.ProductTypeName}) ${product.Name}`
						)}
					</Typography>
					{recipeId && onToggleActive ? (
						<Tooltip
							title={
								!readOnly && isDirty()
									? "Save your changes first."
									: isActive === false && (isDraft || missingCount > 0)
										? "Complete the required fields to activate this draft."
										: ""
							}
						>
							<span>
								<FormControlLabel
									sx={{ ml: 0.5, mr: 0 }}
									control={
										<Switch
											checked={isActive ?? true}
											onChange={(e) => onToggleActive(e.target.checked)}
											size="small"
											color="primary"
											disabled={
												readOnly ||
												isDirty() ||
												(isActive === false && (isDraft || missingCount > 0))
											}
										/>
									}
									label={
										<Typography
											variant="body2"
											sx={{ color: "text.secondary" }}
										>
											Active
										</Typography>
									}
								/>
							</span>
						</Tooltip>
					) : null}
					{recipeId && isDraft ? (
						<Chip size="small" label="Draft" color="warning" sx={{ ml: 0.5 }} />
					) : null}
				</Box>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					{canOpenParameters ? (
						<Tooltip title="Open the global parameter limits settings (new tab)">
							<Link
								component="button"
								type="button"
								underline="hover"
								onClick={openParameters}
								sx={{
									display: "inline-flex",
									alignItems: "center",
									gap: 0.4,
									fontSize: "0.8rem",
									fontWeight: 600,
									whiteSpace: "nowrap",
								}}
							>
								<VisibilityIcon sx={{ fontSize: "1rem" }} />
								Parameter limits
							</Link>
						</Tooltip>
					) : null}
					{recipeId && isCurrent ? (
						<Chip size="small" label="Default" color="success" />
					) : null}
					{recipeId && onSetCurrent && !isCurrent && !readOnly ? (
						<Tooltip
							title={
								isActive === false
									? "Activate the recipe before setting it as default."
									: ""
							}
						>
							<span>
								<Button
									variant="contained"
									color="success"
									size="small"
									onClick={onSetCurrent}
									disabled={isSaving || isActive === false}
								>
									SET AS DEFAULT
								</Button>
							</span>
						</Tooltip>
					) : null}
					{SHOW_IN_PLC && recipeId && onSetPlcCurrent && !isPlcCurrent ? (
						<Button
							color="success"
							variant="outlined"
							size="small"
							onClick={onSetPlcCurrent}
							disabled={isSaving}
						>
							SET PLC CURRENT
						</Button>
					) : null}
					{collapsible ? (
						<Tooltip title="Collapse">
							<IconButton
								size="small"
								onClick={requestClose}
								disabled={isSaving}
							>
								<ExpandLessIcon />
							</IconButton>
						</Tooltip>
					) : (
						<Tooltip title="Close">
							<IconButton
								size="small"
								onClick={requestClose}
								disabled={isSaving}
							>
								<CloseIcon />
							</IconButton>
						</Tooltip>
					)}
				</Box>
			</Box>

			<Divider />

			{/* Start from (create only): single-select chips + copy source. The
			    first thing seen/touched when creating a recipe. */}
			{!recipeId && onPrefill ? (
				<Box sx={{ px: 2, pt: 1.25 }}>
					<Box
						sx={{
							display: "inline-flex",
							flexDirection: "column",
							gap: 0.75,
							maxWidth: "100%",
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 1.5,
							px: 1.5,
							py: 1,
						}}
					>
						<Typography
							variant="caption"
							sx={{
								fontWeight: 700,
								color: "text.secondary",
								letterSpacing: "0.04em",
							}}
						>
							START FROM
						</Typography>
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
							{START_FROM_OPTIONS.map((o) => (
								<Chip
									key={o.mode}
									label={o.label}
									size="small"
									color={prefillMode === o.mode ? "primary" : "default"}
									variant={prefillMode === o.mode ? "filled" : "outlined"}
									onClick={() => requestPrefill(o.mode)}
								/>
							))}
						</Box>
						{prefillMode === "copy" && copySourceLabel ? (
							<Typography variant="caption" color="text.secondary">
								Copying from: <strong>{copySourceLabel}</strong>
							</Typography>
						) : null}
					</Box>
				</Box>
			) : null}

			{/* Fields: recipe name / crew / active. Top-aligned so the Recipe Name
			    error helper text doesn't shift the Crew / Active controls. */}
			<Box
				sx={{
					display: "flex",
					alignItems: "flex-start",
					gap: 1.5,
					px: 2,
					py: 1.25,
					flexWrap: "wrap",
				}}
			>
				<TextField
					size="small"
					required
					label="Recipe Name"
					value={name}
					disabled={readOnly}
					error={!!shownNameError}
					helperText={shownNameError ?? undefined}
					onChange={(e) => setName(e.target.value)}
					slotProps={{ inputLabel: { shrink: true } }}
					sx={{ ...EDITABLE_FIELD_SX, width: 300 }}
				/>
				<TextField
					select
					required
					size="small"
					label="Crew"
					value={crew}
					error={!crew}
					disabled={readOnly}
					onChange={(e) => setCrew(e.target.value)}
					slotProps={{ inputLabel: { shrink: true } }}
					sx={{ ...EDITABLE_FIELD_SX, width: 120 }}
				>
					{CREWS.map((c) => (
						<MenuItem key={c} value={c}>
							{c}
						</MenuItem>
					))}
				</TextField>
				<TextField
					size="small"
					label="Comments"
					value={comments}
					disabled={readOnly}
					multiline
					maxRows={3}
					onChange={(e) => setComments(e.target.value)}
					slotProps={{ inputLabel: { shrink: true } }}
					sx={{ ...EDITABLE_FIELD_SX, flex: 1, minWidth: 220 }}
				/>
				{isSaving && <CircularProgress size={16} />}
			</Box>

			<Divider />

			{/* Body */}
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					overflow: "auto",
					p: 2,
					containerType: "inline-size",
				}}
			>
				{isLoadingValues ? (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							height: "100%",
						}}
					>
						<CircularProgress />
					</Box>
				) : (
					<>
						{/* Two columns: left = the field groups, right = the rolls table
						    plus the product dimensions. The right column takes exactly the
						    width the rolls table needs (`auto`) and the field cards get
						    everything left over, so widening the editor adds field columns
						    instead of padding the numbers. */}
						<Box
							sx={{
								display: "grid",
								gap: 1.5,
								alignItems: "stretch",
								gridTemplateColumns: "1fr",
								"@container (min-width: 760px)": {
									gridTemplateColumns: "minmax(0, 1fr) minmax(0, auto)",
								},
							}}
						>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
								{FIELD_GROUP_ORDER.map((group) => {
									const list = sectionOf(group);
									return list ? (
										<Box key={group}>{renderCard(list)}</Box>
									) : null;
								})}
							</Box>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
								{perRollParams.length ? (
									<Box sx={{ ...cardSx, flex: 1 }}>
										<Box
											onClick={() => toggleSection("StraighteningRolls")}
											sx={cardHeaderSx}
										>
											<Typography sx={{ ...sectionHeaderSx, mt: 0, mb: 0 }}>
												{GROUP_LABELS.StraighteningRolls}
											</Typography>
											<IconButton
												size="small"
												sx={{ p: 0.25, color: "text.secondary" }}
											>
												{collapsed.StraighteningRolls ? (
													<ExpandMoreIcon fontSize="small" />
												) : (
													<ExpandLessIcon fontSize="small" />
												)}
											</IconButton>
										</Box>
										<Collapse in={!collapsed.StraighteningRolls}>
											<Box sx={{ pt: 0.75 }}>
												<StraightenerRollsTable
													params={perRollParams}
													rollCount={rollCount}
													getCell={getRollCell}
													setCell={setRollCell}
													limitOverrides={limitOverrides}
													readOnly={readOnly}
													cellError={rollCellError}
												/>
											</Box>
										</Collapse>
									</Box>
								) : null}
								{dimensionsSection ? renderCard(dimensionsSection) : null}
							</Box>
						</Box>
					</>
				)}
			</Box>

			<Divider />

			{/* Footer */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "flex-end",
					gap: 1,
					px: 2,
					py: 1.5,
				}}
			>
				{isLoadingValues ? null : saveBlocked ? (
					<Typography
						variant="caption"
						color="error"
						sx={{ mr: "auto", fontWeight: 600 }}
					>
						{blockErrorMessage}
					</Typography>
				) : missingCount > 0 ? (
					<Typography
						variant="caption"
						sx={{ mr: "auto", fontWeight: 600, color: "warning.main" }}
					>
						{draftMessage}
					</Typography>
				) : null}
				<Button
					variant="contained"
					onClick={requestClose}
					disabled={isSaving}
					sx={{
						backgroundColor: "#e0e0e0",
						color: "rgba(0, 0, 0, 0.87)",
						"&:hover": { backgroundColor: "#d5d5d5" },
					}}
				>
					CLOSE
				</Button>
				{readOnly ? null : recipeId && onSaveCurrent ? (
					// Editing an existing recipe → overwrite this version in place.
					// Disabled until something actually changes.
					<Button
						color="primary"
						variant="contained"
						onClick={handleSaveCurrent}
						disabled={
							isSaving || !crew || isLoadingValues || saveBlocked || !isDirty()
						}
					>
						SAVE
					</Button>
				) : (
					// New recipe → create it (confirmed first). Blue = just save;
					// green = save and make it the product's current recipe.
					<>
						<Button
							color="primary"
							variant="contained"
							onClick={() =>
								withValidName(() =>
									onSave(
										crew || null,
										name || null,
										commentsOrNull(),
										buildValues(),
									),
								)
							}
							disabled={isSaving || isLoadingValues || !crew || saveBlocked}
						>
							SAVE
						</Button>
						{onSaveAndSetCurrent ? (
							<Button
								color="success"
								variant="contained"
								onClick={() =>
									withValidName(() =>
										onSaveAndSetCurrent(
											crew || null,
											name || null,
											commentsOrNull(),
											buildValues(),
										),
									)
								}
								disabled={
									isSaving ||
									isLoadingValues ||
									!crew ||
									saveBlocked ||
									missingCount > 0
								}
							>
								SAVE & SET DEFAULT
							</Button>
						) : null}
					</>
				)}
			</Box>

			<UnsavedChangesModal
				open={confirmClose}
				onKeepEditing={() => setConfirmClose(false)}
				onDiscard={() => {
					setConfirmClose(false);
					onClose();
				}}
			/>

			{confirmPrefill ? (
				<MesfModal
					id="straightener-recipe-prefill"
					title="Replace current values"
					open={confirmPrefill != null}
					handleClose={() => setConfirmPrefill(null)}
					maxWidth="sm"
				>
					<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
						<Typography>
							You have <strong>unsaved changes</strong>. Changing the starting
							point will <strong>replace the current values</strong>. Continue?
						</Typography>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<Box
							sx={{
								display: "flex",
								justifyContent: "flex-end",
								gap: 2,
								width: "100%",
							}}
						>
							<Button
								variant="contained"
								color="inherit"
								onClick={() => setConfirmPrefill(null)}
							>
								Keep editing
							</Button>
							<Button
								variant="contained"
								color="error"
								onClick={() => {
									if (confirmPrefill) applyPrefill(confirmPrefill);
									setConfirmPrefill(null);
								}}
							>
								Replace values
							</Button>
						</Box>
					</MesfModal.Actions>
				</MesfModal>
			) : null}
		</Box>
	);
};
